/* eslint-disable max-len */
'use client';

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import Link from 'next/link';
// import LanguageSwitcher from './LanguageSwitcher';
import { usePathname } from 'next/navigation';
import {
  // HomeOutlined,
  MenuOutlined,
  CloseOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { twMerge } from 'tailwind-merge';
import { COLORS } from '@/common/constants';

export default function MultiLevelMenu({ menuData, websiteCode, locale }) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openPath, setOpenPath] = useState([]);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(menuData.length);
  const [screenSize, setScreenSize] = useState('lg');

  const pathname = usePathname();
  const menuContainerRef = useRef(null);
  const moreMenuContainerRef = useRef(null);
  const itemRefs = useRef(new Map());
  const homeLinkRef = useRef(null);
  const moreBtnRef = useRef(null);
  const langSwitcherRef = useRef(null);

  useEffect(() => {
    const updateScreenSize = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        setScreenSize('mobile');
      } else if (window.matchMedia('(max-width: 1024px)').matches) {
        setScreenSize('tablet');
      } else if (window.matchMedia('(max-width: 1280px)').matches) {
        setScreenSize('desktop');
      } else {
        setScreenSize('xl');
      }
    };

    updateScreenSize();

    const mediaQueries = [
      window.matchMedia('(max-width: 768px)'),
      window.matchMedia('(max-width: 1024px)'),
      window.matchMedia('(max-width: 1280px)'),
    ];

    mediaQueries.forEach((mq) => mq.addEventListener('change', updateScreenSize));

    return () => {
      mediaQueries.forEach((mq) => mq.removeEventListener('change', updateScreenSize));
    };
  }, []);

  useLayoutEffect(() => {
    const calculateVisibleItems = () => {
      if (!menuContainerRef.current?.parentElement || screenSize === 'mobile') return;

      const homeWidth = homeLinkRef.current?.offsetWidth || 0;
      const langSwitcherWidth = langSwitcherRef.current?.offsetWidth || 0;
      const moreBtnWidth = 40;
      const containerWidth = menuContainerRef.current.parentElement.clientWidth;

      let PADDING_AND_GAPS;
      switch (screenSize) {
        case 'tablet':
          PADDING_AND_GAPS = 16;
          break;
        case 'desktop':
          PADDING_AND_GAPS = 24;
          break;
        case 'xl':
          PADDING_AND_GAPS = 32;
          break;
        default:
          PADDING_AND_GAPS = 16;
      }

      const availableSpace = containerWidth - homeWidth - langSwitcherWidth - PADDING_AND_GAPS;

      let currentWidth = 0;
      let newVisibleCount = 0;
      let allItemsFit = true;

      for (const item of menuData) {
        const itemNode = itemRefs.current.get(item.id);
        if (!itemNode) continue;

        const itemWidth = itemNode.offsetWidth;

        if (currentWidth + itemWidth < availableSpace) {
          currentWidth += itemWidth;
          newVisibleCount++;
        } else {
          allItemsFit = false;
          break;
        }
      }

      if (!allItemsFit) {
        const spaceWithMoreBtn = availableSpace - moreBtnWidth;
        currentWidth = 0;
        newVisibleCount = 0;
        for (const item of menuData) {
          const itemNode = itemRefs.current.get(item.id);
          if (!itemNode) continue;

          const itemWidth = itemNode.offsetWidth;
          if (currentWidth + itemWidth < spaceWithMoreBtn) {
            currentWidth += itemWidth;
            newVisibleCount++;
          } else {
            break;
          }
        }
      }

      setVisibleCount(newVisibleCount);
    };

    const observer = new ResizeObserver(calculateVisibleItems);
    const parentElement = menuContainerRef.current?.parentElement;

    if (parentElement) {
      observer.observe(parentElement);
    }

    calculateVisibleItems();

    return () => {
      if (parentElement) {
        observer.unobserve(parentElement);
      }
    };
  }, [menuData, locale, screenSize]);

  const visibleItems = menuData.slice(0, visibleCount);
  const hiddenItems = menuData.slice(visibleCount);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuContainerRef.current && !moreMenuContainerRef.current.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (slug) => {
    const currentPath = pathname?.replace(/\/+$/, '') || '/';
    const normalizedSlug = slug?.replace(/^\/+|\/+$/g, '');
    const basePath = `${normalizedSlug ? `/${normalizedSlug}` : ''}`;
    if (!normalizedSlug) {
      return currentPath === `/`;
    }
    return currentPath === basePath || currentPath.startsWith(basePath + '/');
  };

  const toggleMobileMenu = () => setIsMobileOpen(!isMobileOpen);

  const handleTogglePath = (level, id) => {
    setOpenPath((prev) => {
      const newPath = [...prev.slice(0, level)];
      if (prev[level] === id) return newPath;
      newPath[level] = id;
      return newPath;
    });
  };

  const renderSubMenu = (children, level = 1, isMobile = false, parentSlug = '') => (
    <ul
      className={`${
        isMobile
          ? 'pl-4 border-l border-gray-200 max-h-[300px] overflow-y-auto'
          : `absolute bg-white text-black shadow-md min-w-[200px] z-50 ${level === 1 ? '-top-0.5 left-0' : 'top-0 left-full'}`
      }`}>
      {children.map((child, index) => {
        const isOpen = openPath[level] === child.id;
        const parentParts = parentSlug.split('/').filter(Boolean);
        const shortSlugParts = [parentParts[0], child.slug].filter(Boolean);
        const fullSlug = shortSlugParts.join('/');
        const linkHrefCheck = child.children?.length > 0 ? '#' : `/${fullSlug}`;
        const linkHref = websiteCode ? `/sub/${websiteCode}${linkHrefCheck}` : linkHrefCheck;

        return (
          <li
            key={child.id}
            className={twMerge(
              'relative',
              isMobile
                ? 'py-2 border-b border-white/30'
                : `px-2 ${index >= 0 && index < children?.length - 1 ? 'border-b border-gray-300' : ''} ${COLORS.greenLightHover} font-[550]`,
            )}
            onMouseEnter={() => !isMobile && handleTogglePath(level, child.id)}
            onMouseLeave={() => !isMobile && handleTogglePath(level, null)}>
            <div className={`flex items-center justify-between w-full relative ${isMobile ? 'pr-10' : 'pr-6'}`}>
              <Link
                href={linkHref}
                className={`block w-full py-2 whitespace-nowrap text-sm ${isActive(fullSlug) && !isMobile ? 'font-bold underline' : ''}`}
                onClick={() => {
                  if (isMobile) setIsMobileOpen(false);
                }}>
                {child.name}
              </Link>
              {child.children?.length > 0 && (
                <span
                  className={`cursor-pointer absolute right-2 text-xs ${isMobile ? 'text-white' : 'text-black'}`}
                  onClick={
                    isMobile
                      ? (e) => {
                          e.preventDefault();
                          handleTogglePath(level, child.id);
                        }
                      : undefined
                  }>
                  {isMobile ? (isOpen ? '-' : '+') : '▸'}
                </span>
              )}
            </div>

            {child.children?.length > 0 && isOpen && <div>{renderSubMenu(child.children, level + 1, isMobile, fullSlug)}</div>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <nav className="container mx-auto bg-[#b71c1c] text-white">
      <div className="flex justify-between items-center p-2 md:hidden border border-gray-200">
        <span className="text-lg font-bold"></span>
        <button onClick={toggleMobileMenu} className="text-2xl text-white">
          {isMobileOpen ? <CloseOutlined /> : <MenuOutlined />}
        </button>
      </div>

      <div className="hidden md:flex justify-between items-center h-[42px]">
        <ul
          className={`flex pl-0 px-4 py-0 items-center relative ${
            screenSize === 'tablet'
              ? 'w-[75%]! space-x-1 h-[42px]'
              : screenSize === 'desktop'
                ? 'w-[78%]! space-x-2 h-[42px]'
                : 'w-[80%]! space-x-3 h-[42px]'
          }`}
          ref={menuContainerRef}>
          {visibleItems.map((item) => (
            <li
              key={item.id}
              className="relative flex items-center "
              onMouseEnter={() => handleTogglePath(0, item.id)}
              onMouseLeave={() => handleTogglePath(0, null)}
              ref={(node) => itemRefs.current.set(item.id, node)}>
              {/* <div className="relative"> */}
              <Link
                href={item.children?.length > 0 ? '#' : websiteCode ? `/sub/${websiteCode}/${item.slug}` : `/${item.slug}`}
                className={twMerge(
                  `py-1 text-[14px] px-4 whitespace-nowrap inline-block uppercase w-full transition-colors duration-200 font-extrabold ${
                    isActive(item.slug) ? 'bg-black/20 text-white h-[42px] flex items-center justify-center' : 'text-white'
                  }`,
                  item.slug === '/' ? 'flex items-center justify-center' : '',
                )}>
                {item.slug === '/' ? <HomeOutlined className="h-6 w-6 text-base mb-1" /> : null}
                {item.name}
              </Link>
              {item.children?.length > 0 && (
                <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-white pointer-events-none">▾</span>
              )}
              {/* </div> */}
              {item.children?.length > 0 && openPath[0] === item.id && (
                <div className="absolute top-full left-0">{renderSubMenu(item.children, 1, false, item.slug)}</div>
              )}
            </li>
          ))}

          <div className="absolute opacity-0 -z-50 pointer-events-none" aria-hidden="true">
            {hiddenItems.map((item) => (
              <li key={`hidden-${item.id}`} ref={(node) => itemRefs.current.set(item.id, node)}>
                <span
                  className={`${
                    screenSize === 'tablet' ? 'text-[14px] px-3 py-3' : screenSize === 'desktop' ? 'text-[14px] px-4 py-3' : 'text-[14px] px-4 py-3'
                  } whitespace-nowrap uppercase font-extrabold`}>
                  {item.name}
                </span>
              </li>
            ))}
          </div>

          {hiddenItems.length > 0 && (
            <li className="relative" ref={moreMenuContainerRef}>
              <button
                ref={moreBtnRef}
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className={`text-[20px] px-3  leading-none cursor-pointer inline-block pb-[15px]! font-bold text-white  transition-colors duration-200 ${
                  isMoreMenuOpen ? 'bg-[#B8351A]' : ''
                }`}
                aria-haspopup="true"
                aria-expanded={isMoreMenuOpen}>
                ...
              </button>

              {isMoreMenuOpen && (
                <ul className="absolute bg-white text-black shadow-lg border border-gray-200 min-w-[220px] z-50 top-full right-0 lg:left-0">
                  {hiddenItems.map((item) => (
                    <li
                      key={item.id}
                      className="relative hover:bg-gray-100"
                      onMouseEnter={() => handleTogglePath(0, item.id)}
                      onMouseLeave={() => handleTogglePath(0, null)}>
                      <div className="flex items-center justify-between w-full relative pr-8">
                        <Link
                          href={item.children?.length > 0 ? '#' : websiteCode ? `/sub/${websiteCode}/${item.slug}` : `/${item.slug}`}
                          className={`block w-full px-4 py-3 whitespace-nowrap text-[14px] uppercase font-extrabold ${
                            isActive(item.slug) ? 'bg-[#b71c1c] text-white' : 'text-black hover:text-[#b71c1c]'
                          }`}>
                          {item.name}
                        </Link>
                        {item.children?.length > 0 && <span className="absolute right-3 text-sm text-black pointer-events-none">▸</span>}
                      </div>
                      {item.children?.length > 0 && openPath[0] === item.id && (
                        <div className="absolute top-0 left-full">{renderSubMenu(item.children, 1, false, item.slug)}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
        </ul>
      </div>

      {isMobileOpen && (
        <div className="md:hidden px-4 pb-4 bg-[#7f0000]">
          <ul className="flex flex-col gap-2">
            {/* <li className="border-b border-white/30 pb-2">
              <Link
                href={websiteCode ? `/sub/${websiteCode}` : `/`}
                className="text-white text-sm mt-2 block w-full"
                onClick={() => setIsMobileOpen(false)}>
                <HomeOutlined className="h-6 w-6 text-base" />
              </Link>
            </li> */}

            {menuData.map((item) => {
              const isOpen = openPath[0] === item.id;
              return (
                <li key={item.id} className="border-b border-white/30 pb-2 uppercase">
                  <div className="flex items-center justify-between relative">
                    <Link
                      href={item.children?.length > 0 ? '#' : websiteCode ? `/sub/${websiteCode}/${item.slug}` : `/${item.slug}`}
                      className="text-white text-sm py-2 block w-full"
                      onClick={() => {
                        if (!item.children || item.children.length === 0) {
                          setIsMobileOpen(false);
                        } else {
                          handleTogglePath(0, item.id);
                        }
                      }}>
                      {item.slug === '/' ? <HomeOutlined className="h-6 w-6 text-base" /> : null}
                      {item.name}
                    </Link>
                    {item.children?.length > 0 && (
                      <button onClick={() => handleTogglePath(0, item.id)} className="text-white text-lg px-2">
                        {isOpen ? '-' : '+'}
                      </button>
                    )}
                  </div>
                  {item.children?.length > 0 && isOpen && <div>{renderSubMenu(item.children, 1, true, item.slug)}</div>}
                </li>
              );
            })}
          </ul>
          {/* <div className="mt-4">
            <LanguageSwitcher />
          </div> */}
        </div>
      )}
    </nav>
  );
}
